import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  PlayIcon, 
  // PauseIcon, // Unused for now
  DocumentDuplicateIcon
  // CogIcon // Unused for now
} from '@heroicons/react/24/outline';
import { strategiesApi } from '@/services/api';
import { Strategy } from '@/types';
// import { StrategyPerformance } from '@/types'; // Unused for now
import { formatPercentage, getChangeColor } from '@/utils/formatters';
// import { formatCurrency, formatDate } from '@/utils/formatters'; // Unused for now
import LoadingSpinner from '@/components/UI/LoadingSpinner';
import Modal from '@/components/UI/Modal';
import StrategyForm from '@/components/Forms/StrategyForm';
import ConfirmDialog from '@/components/UI/ConfirmDialog';
import toast from 'react-hot-toast';

interface StrategiesPageState {
  strategies: Strategy[];
  loading: boolean;
  error: string | null;
  selectedStrategy: Strategy | null;
  showForm: boolean;
  showDeleteConfirm: boolean;
  formMode: 'create' | 'edit' | 'clone';
}

const StrategiesPage: React.FC = () => {
  const [state, setState] = useState<StrategiesPageState>({
    strategies: [],
    loading: true,
    error: null,
    selectedStrategy: null,
    showForm: false,
    showDeleteConfirm: false,
    formMode: 'create',
  });

  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: 'all', // all, active, inactive
    tradingMode: 'all', // all, paper, live
  });

  useEffect(() => {
    fetchStrategies();
  }, []);

  // Debug logging for modal state
  useEffect(() => {
    console.log('Modal state changed - showForm:', state.showForm, 'formMode:', state.formMode);
  }, [state.showForm, state.formMode]);

  const fetchStrategies = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const response = await strategiesApi.getAll();
      setState(prev => ({
        ...prev,
        strategies: (response as any).data?.strategies || [],
        loading: false,
      }));
    } catch (error) {
      console.error('Error fetching strategies:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load strategies. Please try again.',
      }));
    }
  };

  const handleCreateStrategy = () => {
    console.log('Create Strategy button clicked!');
    setState(prev => {
      const newState = {
        ...prev,
        selectedStrategy: null,
        formMode: 'create' as const,
        showForm: true,
      };
      console.log('Setting state:', newState);
      return newState;
    });
  };

  const handleEditStrategy = (strategy: Strategy) => {
    setState(prev => ({
      ...prev,
      selectedStrategy: strategy,
      formMode: 'edit',
      showForm: true,
    }));
  };

  const handleCloneStrategy = (strategy: Strategy) => {
    setState(prev => ({
      ...prev,
      selectedStrategy: strategy,
      formMode: 'clone',
      showForm: true,
    }));
  };

  const handleDeleteStrategy = (strategy: Strategy) => {
    setState(prev => ({
      ...prev,
      selectedStrategy: strategy,
      showDeleteConfirm: true,
    }));
  };

  const handleFormSubmit = async (data: Partial<Strategy>) => {
    try {
      if (state.formMode === 'create') {
        await strategiesApi.create(data);
        toast.success('Strategy created successfully');
      } else if (state.formMode === 'edit' && state.selectedStrategy) {
        await strategiesApi.update(state.selectedStrategy.id, data);
        toast.success('Strategy updated successfully');
      } else if (state.formMode === 'clone' && state.selectedStrategy) {
        await strategiesApi.clone(state.selectedStrategy.id, data.name || `${state.selectedStrategy.name} (Copy)`);
        toast.success('Strategy cloned successfully');
      }

      setState(prev => ({ ...prev, showForm: false, selectedStrategy: null }));
      fetchStrategies();
    } catch (error) {
      console.error('Error saving strategy:', error);
      toast.error('Failed to save strategy');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!state.selectedStrategy) return;

    try {
      await strategiesApi.delete(state.selectedStrategy.id);
      toast.success('Strategy deleted successfully');
      setState(prev => ({ ...prev, showDeleteConfirm: false, selectedStrategy: null }));
      fetchStrategies();
    } catch (error) {
      console.error('Error deleting strategy:', error);
      toast.error('Failed to delete strategy');
    }
  };

  const handleToggleStatus = async (strategy: Strategy) => {
    try {
      await strategiesApi.update(strategy.id, { isActive: !strategy.isActive });
      toast.success(`Strategy ${strategy.isActive ? 'deactivated' : 'activated'}`);
      fetchStrategies();
    } catch (error) {
      console.error('Error updating strategy status:', error);
      toast.error('Failed to update strategy status');
    }
  };

  const handleExecuteStrategy = async (strategy: Strategy) => {
    try {
      await strategiesApi.execute(strategy.id);
      toast.success('Strategy execution initiated');
    } catch (error) {
      console.error('Error executing strategy:', error);
      toast.error('Failed to execute strategy');
    }
  };

  const filteredStrategies = state.strategies.filter(strategy => {
    const matchesSearch = strategy.name.toLowerCase().includes(filters.search.toLowerCase()) ||
                         strategy.description?.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesType = !filters.type || strategy.type === filters.type;
    
    const matchesStatus = filters.status === 'all' ||
                         (filters.status === 'active' && strategy.isActive) ||
                         (filters.status === 'inactive' && !strategy.isActive);

    const matchesTradingMode = filters.tradingMode === 'all' ||
                              (filters.tradingMode === 'paper' && strategy.isPaperTrading) ||
                              (filters.tradingMode === 'live' && !strategy.isPaperTrading);

    return matchesSearch && matchesType && matchesStatus && matchesTradingMode;
  });

  // const _strategyTypes = [...new Set(state.strategies.map(s => s.type))]; // Unused for now

  if (state.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-heading-1 font-bold text-primary-custom">Trading Strategies</h1>
          <p className="text-body text-secondary-custom mt-1">
            Create, configure, and manage your automated trading strategies
          </p>
        </div>
        <button
          onClick={handleCreateStrategy}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Strategy
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card rounded-lg shadow-card p-6">
          <h3 className="text-body font-medium text-secondary-custom mb-2">Total Strategies</h3>
          <p className="text-data-large font-bold text-primary-custom">{state.strategies.length}</p>
        </div>
        
        <div className="card rounded-lg shadow-card p-6">
          <h3 className="text-body font-medium text-secondary-custom mb-2">Active Strategies</h3>
          <p className="text-data-large font-bold text-success-600">
            {state.strategies.filter(s => s.isActive).length}
          </p>
        </div>
        
        <div className="card rounded-lg shadow-card p-6">
          <h3 className="text-body font-medium text-secondary-custom mb-2">Paper Trading</h3>
          <p className="text-data-large font-bold text-warning-600">
            {state.strategies.filter(s => s.isPaperTrading).length}
          </p>
        </div>
        
        <div className="card rounded-lg shadow-card p-6">
          <h3 className="text-body font-medium text-secondary-custom mb-2">Live Trading</h3>
          <p className="text-data-large font-bold text-primary-600">
            {state.strategies.filter(s => !s.isPaperTrading && s.isActive).length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card rounded-lg shadow-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-body font-medium text-secondary-custom mb-2">
              Search
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              placeholder="Search strategies..."
              className="form-input form-bg-custom form-border-custom form-text-custom form-placeholder-custom focus-border-custom focus-ring-custom w-full rounded-md"
            />
          </div>
          
          <div>
            <label className="block text-body font-medium text-secondary-custom mb-2">
              Strategy Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="form-input form-bg-custom form-border-custom form-text-custom focus-border-custom focus-ring-custom w-full rounded-md"
            >
              <option value="">All Types</option>
              <option value="ma_crossover">MA Crossover</option>
              <option value="momentum">Momentum</option>
              <option value="balanced_portfolio">Balanced Portfolio</option>
              <option value="ai_enhanced">AI Enhanced</option>
            </select>
          </div>
          
          <div>
            <label className="block text-body font-medium text-secondary-custom mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="form-input form-bg-custom form-border-custom form-text-custom focus-border-custom focus-ring-custom w-full rounded-md"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div>
            <label className="block text-body font-medium text-secondary-custom mb-2">
              Trading Mode
            </label>
            <select
              value={filters.tradingMode}
              onChange={(e) => setFilters(prev => ({ ...prev, tradingMode: e.target.value }))}
              className="form-input form-bg-custom form-border-custom form-text-custom focus-border-custom focus-ring-custom w-full rounded-md"
            >
              <option value="all">All Modes</option>
              <option value="paper">Paper Trading</option>
              <option value="live">Live Trading</option>
            </select>
          </div>
        </div>
      </div>

      {/* Strategies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStrategies.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <div className="text-secondary-custom mb-4">
              {state.strategies.length === 0 ? 'No strategies found' : 'No strategies match your filters'}
            </div>
            {state.strategies.length === 0 && (
              <button
                onClick={handleCreateStrategy}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md"
              >
                Create Your First Strategy
              </button>
            )}
          </div>
        ) : (
          filteredStrategies.map((strategy) => (
            <div key={strategy.id} className="card rounded-lg shadow-card p-6 hover:shadow-card-hover transition-shadow">
              {/* Strategy Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-heading-3 font-medium text-primary-custom mb-1">
                    {strategy.name}
                  </h3>
                  <p className="text-body text-secondary-custom mb-2">
                    {strategy.description || 'No description'}
                  </p>
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex px-2 py-1 text-caption font-semibold rounded-full bg-secondary-custom text-secondary-custom">
                      {strategy.type.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className={`inline-flex px-2 py-1 text-caption font-semibold rounded-full ${
                      strategy.isPaperTrading 
                        ? 'bg-warning-100 text-warning-800' 
                        : 'bg-primary-100 text-primary-800'
                    }`}>
                      {strategy.isPaperTrading ? 'Paper' : 'Live'}
                    </span>
                    <button
                      onClick={() => handleToggleStatus(strategy)}
                      className={`inline-flex px-2 py-1 text-caption font-semibold rounded-full ${
                        strategy.isActive
                          ? 'bg-success-100 text-success-800'
                          : 'bg-secondary-custom text-secondary-custom'
                      }`}
                    >
                      {strategy.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Performance Metrics */}
              {strategy.performance && (
                <div className="mb-4 p-3 bg-secondary-custom rounded-md">
                  <div className="grid grid-cols-2 gap-4 text-body">
                    <div>
                      <span className="text-secondary-custom">Total Return:</span>
                      <span className={`ml-1 font-medium ${getChangeColor(strategy.performance.totalReturnPercentage)}`}>
                        {formatPercentage(strategy.performance.totalReturnPercentage)}
                      </span>
                    </div>
                    <div>
                      <span className="text-secondary-custom">Win Rate:</span>
                      <span className="ml-1 font-medium text-primary-custom">
                        {formatPercentage(strategy.performance.winRate)}
                      </span>
                    </div>
                    <div>
                      <span className="text-secondary-custom">Sharpe Ratio:</span>
                      <span className="ml-1 font-medium text-primary-custom">
                        {strategy.performance.sharpeRatio.toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="text-secondary-custom">Max Drawdown:</span>
                      <span className="ml-1 font-medium text-red-600">
                        {formatPercentage(strategy.performance.maxDrawdown)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Strategy Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-primary-custom">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleExecuteStrategy(strategy)}
                    disabled={!strategy.isActive}
                    className="text-primary-600 hover:text-primary-900 disabled:text-tertiary-custom"
                    title="Execute Strategy"
                  >
                    <PlayIcon className="h-4 w-4" />
                  </button>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditStrategy(strategy)}
                    className="text-primary-600 hover:text-primary-900"
                    title="Edit"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleCloneStrategy(strategy)}
                    className="text-secondary-custom hover-text-primary-custom"
                    title="Clone"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteStrategy(strategy)}
                    className="text-danger-600 hover:text-danger-900"
                    title="Delete"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modals */}
      <Modal
        isOpen={state.showForm}
        onClose={() => setState(prev => ({ ...prev, showForm: false, selectedStrategy: null }))}
        title={
          state.formMode === 'create' ? 'Create Strategy' :
          state.formMode === 'edit' ? 'Edit Strategy' : 'Clone Strategy'
        }
        size="xl"
      >
        <StrategyForm
          initialData={state.selectedStrategy}
          mode={state.formMode}
          onSubmit={handleFormSubmit}
          onCancel={() => setState(prev => ({ ...prev, showForm: false, selectedStrategy: null }))}
        />
      </Modal>

      <ConfirmDialog
        isOpen={state.showDeleteConfirm}
        onClose={() => setState(prev => ({ ...prev, showDeleteConfirm: false, selectedStrategy: null }))}
        onConfirm={handleDeleteConfirm}
        title="Delete Strategy"
        message={`Are you sure you want to delete the strategy "${state.selectedStrategy?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="bg-danger-600 hover:bg-danger-700"
      />
    </div>
  );
};

export default StrategiesPage; 